import BaseStore from 'bz-stores/mobx/base-store';
import type { IEvent } from 'bz-utilities/modules/plugins/register/stores/mediator';
import { action, autorun, computed, observable, observe, runInAction } from 'mobx';
import type { IReactionDisposer, Lambda } from 'mobx';
import Moment from 'moment';
import Api from 'tm3-core/api';
import { AppointmentGetRequest, DiaryRequest, MonthlyScheduleRequest } from 'tm3-core/api.dtos';
import type {
  AppointmentDetailsResponse,
  DiaryDefaultDataForUserResponse,
  GeneralPractitionerResponse,
  ResourceMode
} from 'tm3-core/api.dtos';
import { MonthlyScheduleResponse } from 'tm3-core/api.dtos';
import { cache } from 'tm3-core/cache';
import { ConfigObservable } from './config';
import DiaryCacheStore from './diary-cache-store';
import Resources from './diary-view-store/enums/resources';
import CalendarDisplay from './enums/calendar-display';
import ScheduleStore from './schedule-store';

export enum ETimeFormat {
  h12 = '12h',
  h24 = '24h'
}

export enum EMode {
  appointment = 'appointment',
  event = 'event',
  session = 'session'
}

export class DiaryViewStoreType {
  [key: string]: any;
  @observable lsPrefix = '';
  @observable mode: EMode = EMode.appointment;
  @observable isWeek = false;
  @observable calendarDisplay: CalendarDisplay = CalendarDisplay.Day;
  @observable monthOverview: MonthlyScheduleResponse = new MonthlyScheduleResponse();
  @observable hideDayWeekSelector = false;
  @observable timeFormat: ETimeFormat = ETimeFormat.h24;
  @observable privacy = false;
  @observable interval = 15;
  @observable resourceMode: Resources = Resources.practitioner;
  @observable cancellations = false;
  @observable location = -1;
  @observable rooms = observable.array<number>([-2]);
  @observable readonly practitioners = observable.array<number>([]);
  @observable date: string = new Date().toJSON();
  @observable currentTime = new Date();
  @observable processing = true;
  @observable withoutSessions = true;
  @observable weekends = false;
  @observable compressed = true;
  @observable showCalendarSidebar = false;
  @observable pinCalendar = false;
  @observable locationPractitioners = {};
  @observable statsKeys = [];
  @observable loaded: boolean = false;

  //hack for appt selection
  @observable target = '';

  //hacked back prop to stop double fetch
  //investigate computed setters to solve this
  @observable _location: number | undefined = -1;

  @observable readonly _practitioners = observable.array<GeneralPractitionerResponse>([]);

  onClearSelection?: () => void;

  private _disposers: (IReactionDisposer | Lambda)[] = [];
  private configDisposer?: Lambda;
  private lastFetchDate?: Moment.Moment;

  constructor() {
    //sync update with the minute
    let delay = (60 - new Date().getSeconds()) * 1000;
    setTimeout(() => {
      this.currentTime = new Date();
      setInterval(() => {
        this.currentTime = new Date();
      }, 60000);
    }, delay);
  }

  @action.bound
  async onLoad(userId?: number): Promise<void> {
    if (userId !== undefined && userId > 0) {
      this.lsPrefix = userId.toString();
    }

    const res = await BaseStore.defaultClient.post(new DiaryRequest({}));

    runInAction(() => {
      this._onLoad(res);
      this.loaded = true;
    });
  }

  async onReload(userId?: number): Promise<void> {
    //temp hack until ui refactor
    this._disposers.forEach((fn) => {
      fn();
    });
    await this.onLoad(userId);
  }

  @action.bound
  hideDayWeekSelection(): void {
    if (!this.allowAllPractitionerWeekView) {
      if (this.practitioners.length > 1) {
        this.isWeek = false;
        this.hideDayWeekSelector = true;
      }
      if (this.practitioners.length === 1) {
        this.hideDayWeekSelector = false;
      }
    }
  }

  @action.bound
  verifyResources(locationId?: number) {
    let { allRooms, allPractitionerLocations } = DiaryCacheStore;
    const loc = locationId || this._location;

    let availRooms = allRooms.filter((x) => x.locationId === loc).map((x) => x.id);

    this.rooms.replace(this.getStorage(`roomLocations-${this.location}`, this.rooms));

    let availPract = allPractitionerLocations
      .filter((x) => x.locationId === loc)
      .map((x) => x.practitionerId);
    availPract = this.intersect(
      this._practitioners.map((x) => x.id),
      this.intersect(this.activePractitioners, availPract)
    );

    const practitionerLocationsResult = this.getStorage(
      `practitionerLocations-${this.location}`,
      this.practitioners.slice()
    );
    const plr = Array.isArray(practitionerLocationsResult)
      ? practitionerLocationsResult
      : [practitionerLocationsResult];
    let _pract = availPract.length > 0 ? this.intersect(availPract, plr) : plr;
    let _rooms = this.intersect(availRooms, this.rooms);
    if (_rooms.length === 0) {
      _rooms = [-2];
    }
    if (_pract.length === 0 && availPract.length > 0) {
      _pract = [availPract[0]];
    }

    this.rooms.replace(_rooms);
    this.practitioners.replace(_pract);

    if (loc != this._location) {
      this._location = locationId;
    }
  }

  @action
  _onLoad(diary: DiaryDefaultDataForUserResponse) {
    const { isClick } = process.env;
    const isPhone = !!window.mobileDetection.mobile();

    const defaultLocation = !!diary.currentLocation
      ? diary.currentLocation.id
      : this._location || 1;

    this._location = this.getStorage('location', defaultLocation);

    this._practitioners.replace(diary.practitioners);

    this._disposers.forEach((fn) => {
      fn();
    });
    const defaultToWeek = isClick && !isPhone;
    this._disposers = [
      this.applyStorage('isWeek', defaultToWeek),
      this.applyStorage(
        'calendarDisplay',
        defaultToWeek ? CalendarDisplay.Week : CalendarDisplay.Day
      ),
      this.applyStorage('weekends', isClick),
      this.applyStorage('timeFormat', isClick ? '12h' : '24h'),
      this.applyStorage('date', new Date()),
      this.applyStorage('rooms', [diary.currentRoom.id]),
      this.applyStorage('privacy', false),
      this.applyStorage('interval', 15),
      this.applyStorage('location', defaultLocation),
      this.applyStorage('resourceMode', 'practitioner'),
      this.applyStorage(
        'practitioners',
        diary.currentPractitioner ? [diary.currentPractitioner.id] : []
      ),
      this.applyStorage('cancellations', true),
      this.applyStorage('withoutSessions', true),
      autorun(this.onFetch.bind(this, false)),
      (this.configDisposer = observe(ConfigObservable.configMap, (change) => {
        if (change.name === 'ALLOWALLPRACTWEEKVIEW') {
          this.hideDayWeekSelection();
        }
      }))
    ];

    const doVerify = () => {
      runInAction(() => {
        this.verifyResources();
      });
      this._disposers.push(
        observe(this, 'location', (change) => {
          runInAction(() => {
            this.verifyResources(change.newValue);
          });
        })
      );
    };

    if (cache.practitionerLocations.loaded) doVerify();
    else
      this._disposers.push(
        observe(cache.practitionerLocations, 'loaded', () => {
          doVerify();
        })
      );
  }

  intersect<T>(available: T[], current: T[]): T[] {
    const all = new Set(available);
    const sub = new Set(current);
    return [...all].filter((x) => sub.has(x));
  }

  applyStorage(key: string, fallback: any) {
    this[key] = this.getStorage(key, fallback);

    return autorun(() => {
      let val: any = this[key];
      let result = !!val?.toJS ? val.toJS() : val;
      if (!!sessionStorage.getItem('diary-restore')) {
        return;
      }
      localStorage[this.lsPrefix + key] = JSON.stringify(result);
    });
  }

  getStorage(key: string, fallback: any) {
    var result = localStorage[this.lsPrefix + key];
    if (typeof result !== 'string' || result === '') {
      return fallback;
    }

    try {
      result = JSON.parse(result);
    } catch (err) {
      /*supress catch & fallback to string*/
    }

    return result;
  }

  @computed
  get getConfig(): Record<string, string> {
    const { config } = ConfigObservable;
    const diaryPrintState = localStorage.getItem('diaryPrintState');

    if (diaryPrintState !== null) {
      const lsConfig: Record<string, string> | null = JSON.parse(diaryPrintState);
      return lsConfig != null ? lsConfig : config;
    }

    return config;
  }

  @computed
  get activePractitioners(): number[] {
    return DiaryCacheStore.allPractitioners.filter((x) => x.status !== 0).map((x) => x.id);
  }

  @computed
  get workingWeekDays(): number {
    return parseInt(this.getConfig['WORKINGWEEKDAYS']);
  }

  @computed
  get allowAllPractitionerWeekView(): boolean {
    return (this.getConfig['ALLOWALLPRACTWEEKVIEW'] || '').toLowerCase() === 'y';
  }

  @computed
  get firstDayOfWeek(): number {
    return parseInt(this.getConfig['FIRSTDAYOFWEEK']);
  }

  @computed
  get todayLine(): boolean {
    return (this.getConfig['SHOWDIARYTODAYLINE'] || '').toLowerCase() === 'true';
  }

  @computed
  get showDoNextScreen(): boolean {
    return (this.getConfig['DONEXTENABLED'] || '').toLowerCase() === 'true';
  }

  @computed
  get multiDay(): boolean {
    return Math.log2(this.workingWeekDays) % 1 > 0;
  }

  @action.bound
  setDiarySessionMode(practitionerId: number, locationId: number, userId: number) {
    if (!this.loaded) {
      this.onLoad(userId);
    }
    let _cache = Object.assign({}, this);
    _cache._practitionerId = practitionerId;
    sessionStorage.setItem('diary-restore', JSON.stringify(_cache));

    this.practitioners.replace([practitionerId]);
    this._location = locationId;
    this.location = locationId;
    this.mode = EMode.session;
    this.interval = 60;
    this.withoutSessions = true;
    this.isWeek = true;

    this.onFetch();
  }

  @action.bound
  restoreDiaryMode(): void {
    let json = sessionStorage.getItem('diary-restore');
    if (this.mode !== EMode.session) {
      return;
    }
    let restore = JSON.parse(json || '{}');
    restore.mode = 'appointment';

    sessionStorage.removeItem('diary-restore');

    Object.keys(restore).map((key) => {
      if (key.indexOf('_') === 0) {
        return;
      }
      this[key] = restore[key];
    });
  }

  onFetch(isAutoRefresh = false): void {
    let { date, isWeek, resourceMode, weekends, _location, withoutSessions } = this;
    if (![Resources.room, Resources.practitioner, Resources.list].includes(resourceMode)) return;

    //allows fetch when config changes
    let firstDay = this.firstDayOfWeek;
    let workingDays = this.workingWeekDays;
    if (!firstDay || !workingDays) return;
    //-------------------------------
    this.processing = true;
    this.lastFetchDate = Moment();

    let resourceIds = resourceMode === 'room' ? this.rooms : this.practitioners;
    if (process.env.isClick) {
      if (resourceIds.length === 0) {
        resourceIds =
          this.activePractitioners.length > 0
            ? observable.array(this.activePractitioners)
            : observable.array([1]);
      }
    }

    if (this.calendarDisplay === CalendarDisplay.Month) {
      BaseStore.defaultClient
        .post(
          new MonthlyScheduleRequest({
            date: date,
            mode: resourceMode as unknown as ResourceMode,
            location: _location,
            resourceIds: resourceIds,
            autoRefresh: isAutoRefresh,
            showCancellations: this.cancellations
          })
        )
        .then(
          action((res) => {
            this.monthOverview = res.result;
            this.processing = false;
          })
        );
      return;
    }

    const request = {
      date: date,
      isWeek: isWeek,
      mode: resourceMode,
      location: _location,
      showWeekends: weekends,
      resourceIds: resourceIds,
      withoutSessions: withoutSessions
    };

    const queryString = !!isAutoRefresh ? '?autoRefresh=true' : '';
    Api.post('/api/diary/schedule' + queryString, request).then(
      action((res) => {
        this.statsKeys = res.body.result.keys;
        ScheduleStore.onLoad(res.body.result);
        cache.mediator.publish(new DiaryFetchEvent());
        this.processing = false;
      })
    );
  }

  @action.bound
  onUnload(): void {
    this.mode = EMode.appointment;
    this._disposers.forEach((fn) => {
      fn();
    });
  }

  @action.bound
  async _getAppt(apptId: number): Promise<AppointmentDetailsResponse> {
    const { result } = await BaseStore.defaultClient.get(new AppointmentGetRequest({ id: apptId }));

    return result;
  }

  @action.bound
  async moveToAppt(apptId: number): Promise<AppointmentDetailsResponse> {
    const { result } = await BaseStore.defaultClient.get(new AppointmentGetRequest({ id: apptId }));

    runInAction(() => {
      this.location = result.locationId;
      this.practitioners.replace([result.practitionerId]);
      this.date = Moment(result.startDateTime).toDate();
    });

    return result;
  }

  @action.bound
  protected _moveToAppt(appt: AppointmentDetailsResponse): void {
    this.location = appt.locationId;
    if (!this.practitioners.includes(appt.practitionerId)) {
      if (this.practitioners.length > 1) this.practitioners.push(appt.practitionerId);
      else this.practitioners.replace([appt.practitionerId]);
    }
    this.date = Moment(appt.startDateTime).toDate();
  }

  @action.bound
  async navigateToAppt(apptId: number): Promise<void> {
    this.target = `evt-appt-${apptId}`;

    const res = await this._getAppt(apptId);

    this._moveToAppt(res);
  }
}

export { default as Resources } from './diary-view-store/enums/resources';
const DiaryViewStore = new DiaryViewStoreType();
export default DiaryViewStore;

export class DiaryFetchEvent implements IEvent {
  public constructor(init?: Partial<DiaryFetchEvent>) {
    (Object as any).assign(this, init);
  }
}
